import { useEffect, useState, useCallback } from 'react';
import { DashboardLayout } from '@/backoffice/components/layout';
import { Card, Button, Tabs, TabPanel, Input, Badge } from '@/backoffice/components/ui';
import { useAdminContext } from '@/backoffice/hooks/useAdminContext';
import {
  fetchZones,
  fetchCategories,
  fetchAccountTypes,
  createZone,
  deleteZone,
  createCategory,
  deleteCategory,
  createAccountType,
  deleteAccountType,
  fetchDeliveryTypes,
  createDeliveryType,
  deleteDeliveryType,
  fetchPaymentMethods,
  createPaymentMethod,
  deletePaymentMethod,
} from '@/domains/admin/services/admin.service';
import type { ConfigCategory, ConfigReference, ConfigZone } from '@/domains/admin/services/admin.service';
import type { ApiDeliveryType, ApiPaymentMethod } from '@/domains/api/types';
import { MapPin, FolderOpen, Users, Plus, Trash2, Tag, Truck, CreditCard, Lock } from 'lucide-react';

const SYSTEM_ACCOUNT_TYPES = new Set([
  'admin',
  'super_admin',
  'client',
  'community_delivery',
  'professional_delivery',
  'company',
  'relay_point',
]);

export function SuperConfigPage() {
  const userType = useAdminContext();
  const [zones, setZones] = useState<ConfigZone[]>([]);
  const [categories, setCategories] = useState<ConfigCategory[]>([]);
  const [accountTypes, setAccountTypes] = useState<ConfigReference[]>([]);
  const [deliveryTypes, setDeliveryTypes] = useState<ApiDeliveryType[]>([]);
  const [paymentMethods, setPaymentMethods] = useState<ApiPaymentMethod[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const [newZone, setNewZone] = useState('');
  const [newCategory, setNewCategory] = useState('');
  const [newAccountType, setNewAccountType] = useState('');
  const [newDeliveryType, setNewDeliveryType] = useState({ name: '', amount_per_kg: '', description: '' });
  const [newPaymentMethod, setNewPaymentMethod] = useState({ name: '', description: '' });

  const loadAll = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [z, c, t, dt, pm] = await Promise.all([
        fetchZones(),
        fetchCategories(),
        fetchAccountTypes(),
        fetchDeliveryTypes(),
        fetchPaymentMethods(),
      ]);
      setZones(z);
      setCategories(c);
      setAccountTypes(t);
      setDeliveryTypes(dt);
      setPaymentMethods(pm);
    } catch {
      setError('Impossible de charger la configuration.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadAll();
  }, [loadAll]);

  const tabs = [
    { key: 'zones', label: 'Zones', badge: zones.length },
    { key: 'categories', label: 'Catégories documents', badge: categories.length },
    { key: 'types', label: 'Types de compte', badge: accountTypes.length },
    { key: 'delivery', label: 'Types de livraison', badge: deliveryTypes.length },
    { key: 'payments', label: 'Modes de paiement', badge: paymentMethods.length },
  ];

  const handleCreateZone = async () => {
    if (!newZone.trim()) return;
    setSaving(true);
    try {
      await createZone(newZone.trim());
      setNewZone('');
      await loadAll();
    } catch {
      setError('Échec de la création de la zone.');
    } finally {
      setSaving(false);
    }
  };

  const handleCreateCategory = async () => {
    if (!newCategory.trim()) return;
    setSaving(true);
    try {
      await createCategory({ name: newCategory.trim(), type: 'Document' });
      setNewCategory('');
      await loadAll();
    } catch {
      setError('Échec de la création de la catégorie.');
    } finally {
      setSaving(false);
    }
  };

  const handleCreateAccountType = async () => {
    if (!newAccountType.trim()) return;
    setSaving(true);
    try {
      await createAccountType(newAccountType.trim());
      setNewAccountType('');
      await loadAll();
    } catch {
      setError('Échec de la création du type de compte.');
    } finally {
      setSaving(false);
    }
  };

  const handleCreateDeliveryType = async () => {
    if (!newDeliveryType.name.trim() || !newDeliveryType.amount_per_kg) return;
    setSaving(true);
    try {
      await createDeliveryType({
        name: newDeliveryType.name.trim(),
        amount_per_kg: parseFloat(newDeliveryType.amount_per_kg),
        description: newDeliveryType.description.trim() || undefined,
      });
      setNewDeliveryType({ name: '', amount_per_kg: '', description: '' });
      await loadAll();
    } catch {
      setError('Échec de la création du type de livraison.');
    } finally {
      setSaving(false);
    }
  };

  const handleCreatePaymentMethod = async () => {
    if (!newPaymentMethod.name.trim()) return;
    setSaving(true);
    try {
      await createPaymentMethod({
        name: newPaymentMethod.name.trim(),
        description: newPaymentMethod.description.trim() || undefined,
      });
      setNewPaymentMethod({ name: '', description: '' });
      await loadAll();
    } catch {
      setError('Échec de la création du mode de paiement.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <DashboardLayout userType={userType} title="Configuration">
      {error && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">{error}</div>
      )}

      {loading ? (
        <p className="text-center text-gray-500 py-12">Chargement...</p>
      ) : (
        <Tabs tabs={tabs}>
          <TabPanel>
            <Card className="mb-6">
              <div className="flex items-center gap-2 mb-4">
                <MapPin className="w-5 h-5 text-violet-600" />
                <h3 className="font-semibold text-gray-900">Zones de livraison</h3>
              </div>
              <div className="flex gap-3 mb-4">
                <Input
                  placeholder="Nom de la zone"
                  value={newZone}
                  onChange={(e) => setNewZone(e.target.value)}
                  className="max-w-xs"
                />
                <Button leftIcon={<Plus className="w-4 h-4" />} isLoading={saving} onClick={handleCreateZone}>
                  Ajouter
                </Button>
              </div>
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3">
                {zones.map((zone) => (
                  <div key={zone.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border border-gray-200">
                    <span className="font-medium text-gray-900">{zone.name}</span>
                    <button
                      type="button"
                      onClick={async () => { await deleteZone(zone.id); await loadAll(); }}
                      className="p-1.5 rounded hover:bg-red-50 text-red-600"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                ))}
              </div>
            </Card>
          </TabPanel>

          <TabPanel>
            <Card className="mb-6">
              <div className="flex items-center gap-2 mb-4">
                <FolderOpen className="w-5 h-5 text-violet-600" />
                <h3 className="font-semibold text-gray-900">Catégories de documents</h3>
              </div>
              <div className="flex gap-3 mb-4">
                <Input
                  placeholder="Ex: Permis de conduire"
                  value={newCategory}
                  onChange={(e) => setNewCategory(e.target.value)}
                  className="max-w-sm"
                />
                <Button leftIcon={<Plus className="w-4 h-4" />} isLoading={saving} onClick={handleCreateCategory}>
                  Ajouter
                </Button>
              </div>
              <div className="space-y-2">
                {categories.map((cat) => (
                  <div key={cat.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border border-gray-200">
                    <div className="flex items-center gap-2">
                      <Tag className="w-4 h-4 text-gray-400" />
                      <span className="font-medium text-gray-900">{cat.name}</span>
                      <Badge variant="default" size="sm">{cat.type}</Badge>
                    </div>
                    <button
                      type="button"
                      onClick={async () => { await deleteCategory(cat.id); await loadAll(); }}
                      className="p-1.5 rounded hover:bg-red-50 text-red-600"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                ))}
              </div>
            </Card>
          </TabPanel>

          <TabPanel>
            <Card>
              <div className="flex items-center gap-2 mb-4">
                <Users className="w-5 h-5 text-violet-600" />
                <h3 className="font-semibold text-gray-900">Types de compte</h3>
              </div>
              <p className="text-sm text-gray-500 mb-4">
                Rôles applicatifs (client, livreur, admin, super_admin, company…)
              </p>
              <div className="flex gap-3 mb-4">
                <Input
                  placeholder="Code du type (ex: admin)"
                  value={newAccountType}
                  onChange={(e) => setNewAccountType(e.target.value)}
                  className="max-w-xs"
                />
                <Button leftIcon={<Plus className="w-4 h-4" />} isLoading={saving} onClick={handleCreateAccountType}>
                  Ajouter
                </Button>
              </div>
              <div className="grid md:grid-cols-2 gap-2">
                {accountTypes.map((type) => {
                  const isSystemType = SYSTEM_ACCOUNT_TYPES.has(type.name);
                  return (
                  <div key={type.id} className="flex items-center justify-between p-3 bg-violet-50 rounded-lg border border-violet-100">
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-violet-900">{type.name}</span>
                      {isSystemType && (
                        <span className="inline-flex items-center gap-1 text-xs text-violet-600">
                          <Lock className="w-3 h-3" /> Système
                        </span>
                      )}
                    </div>
                    {!isSystemType && (
                      <button
                        type="button"
                        onClick={async () => { await deleteAccountType(type.id); await loadAll(); }}
                        className="p-1.5 rounded hover:bg-red-50 text-red-600"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                  );
                })}
              </div>
            </Card>
          </TabPanel>

          <TabPanel>
            <Card>
              <div className="flex items-center gap-2 mb-4">
                <Truck className="w-5 h-5 text-violet-600" />
                <h3 className="font-semibold text-gray-900">Types de livraison</h3>
              </div>
              <p className="text-sm text-gray-500 mb-4">
                Tarification par kilogramme utilisée pour le calcul des frais de livraison.
              </p>
              <div className="flex flex-wrap gap-3 mb-4">
                <Input
                  placeholder="Nom (ex: Express)"
                  value={newDeliveryType.name}
                  onChange={(e) => setNewDeliveryType({ ...newDeliveryType, name: e.target.value })}
                  className="max-w-xs"
                />
                <Input
                  placeholder="Montant / kg (GNF)"
                  type="number"
                  min="0"
                  step="0.01"
                  value={newDeliveryType.amount_per_kg}
                  onChange={(e) => setNewDeliveryType({ ...newDeliveryType, amount_per_kg: e.target.value })}
                  className="max-w-[160px]"
                />
                <Input
                  placeholder="Description (optionnel)"
                  value={newDeliveryType.description}
                  onChange={(e) => setNewDeliveryType({ ...newDeliveryType, description: e.target.value })}
                  className="max-w-sm"
                />
                <Button leftIcon={<Plus className="w-4 h-4" />} isLoading={saving} onClick={handleCreateDeliveryType}>
                  Ajouter
                </Button>
              </div>
              <div className="space-y-2">
                {deliveryTypes.map((dt) => (
                  <div key={dt.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border border-gray-200">
                    <div>
                      <span className="font-medium text-gray-900">{dt.name}</span>
                      <span className="ml-2 text-sm text-gray-500">
                        {dt.amount_per_kg} GNF/kg
                      </span>
                      {dt.description && (
                        <p className="text-sm text-gray-500 mt-0.5">{dt.description}</p>
                      )}
                    </div>
                    <button
                      type="button"
                      onClick={async () => { await deleteDeliveryType(dt.id); await loadAll(); }}
                      className="p-1.5 rounded hover:bg-red-50 text-red-600"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                ))}
                {deliveryTypes.length === 0 && (
                  <p className="text-sm text-gray-500">Aucun type de livraison configuré.</p>
                )}
              </div>
            </Card>
          </TabPanel>

          <TabPanel>
            <Card>
              <div className="flex items-center gap-2 mb-4">
                <CreditCard className="w-5 h-5 text-violet-600" />
                <h3 className="font-semibold text-gray-900">Modes de paiement</h3>
              </div>
              <div className="flex flex-wrap gap-3 mb-4">
                <Input
                  placeholder="Nom (ex: Mobile Money)"
                  value={newPaymentMethod.name}
                  onChange={(e) => setNewPaymentMethod({ ...newPaymentMethod, name: e.target.value })}
                  className="max-w-xs"
                />
                <Input
                  placeholder="Description (optionnel)"
                  value={newPaymentMethod.description}
                  onChange={(e) => setNewPaymentMethod({ ...newPaymentMethod, description: e.target.value })}
                  className="max-w-sm"
                />
                <Button leftIcon={<Plus className="w-4 h-4" />} isLoading={saving} onClick={handleCreatePaymentMethod}>
                  Ajouter
                </Button>
              </div>
              <div className="space-y-2">
                {paymentMethods.map((pm) => (
                  <div key={pm.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border border-gray-200">
                    <div>
                      <span className="font-medium text-gray-900">{pm.name}</span>
                      {pm.description && (
                        <p className="text-sm text-gray-500 mt-0.5">{pm.description}</p>
                      )}
                    </div>
                    <button
                      type="button"
                      onClick={async () => { await deletePaymentMethod(pm.id); await loadAll(); }}
                      className="p-1.5 rounded hover:bg-red-50 text-red-600"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                ))}
                {paymentMethods.length === 0 && (
                  <p className="text-sm text-gray-500">Aucun mode de paiement configuré.</p>
                )}
              </div>
            </Card>
          </TabPanel>
        </Tabs>
      )}
    </DashboardLayout>
  );
}
